import { useEffect, useState } from "react";
import axios from "axios";
import { Star, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import { BRAND } from "../lib/site-data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GoogleReviews = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/reviews`)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch(() => {
        if (alive) setData({ configured: false, reviews: [] });
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (loading) return null;

  const hasReviews = data?.configured && data?.reviews?.length > 0;

  // Fallback CTA when API is configured but no reviews returned (e.g., listing not indexed yet)
  if (!hasReviews) {
    return (
      <section
        className="py-14 lg:py-20 bg-[#2a3744]"
        data-testid="google-reviews-cta"
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
          <div className="flex justify-center mb-3">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={16}
                strokeWidth={0}
                fill="#D4AF37"
                className="text-[#D4AF37]"
              />
            ))}
          </div>
          <h2 className="font-serif text-2xl sm:text-3xl text-white tracking-tight">
            Read our reviews on Google.
          </h2>
          <p className="mt-3 text-sm text-slate-400 max-w-md mx-auto">
            See what homeowners across Manatee, Sarasota, and Charlotte County
            are saying about working with DN Flooring.
          </p>
          <a
            href={BRAND.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] font-semibold text-[12px] uppercase tracking-[0.16em] hover:bg-[#D4AF37] hover:text-[#1f2935] transition-colors"
            data-testid="google-reviews-cta-link"
          >
            View on Google
            <ExternalLink size={13} strokeWidth={2} />
          </a>
        </div>
      </section>
    );
  }

  // Real reviews
  return (
    <section
      className="py-14 lg:py-20 bg-[#2a3744]"
      data-testid="google-reviews-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl text-white tracking-tight leading-[1.05]">
              Google reviews.
            </h2>
            {data.rating != null && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star
                      key={i}
                      size={14}
                      strokeWidth={0}
                      fill={
                        i < Math.round(data.rating) ? "#D4AF37" : "#334155"
                      }
                      className={
                        i < Math.round(data.rating)
                          ? "text-[#D4AF37]"
                          : "text-[#334155]"
                      }
                    />
                  ))}
                </div>
                <span className="text-[12px] text-slate-400">
                  {data.rating.toFixed(1)} · {data.total} reviews
                </span>
              </div>
            )}
          </div>
          <a
            href={BRAND.googleMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[12px] uppercase tracking-[0.16em] text-[#D4AF37] hover:text-white transition-colors self-start"
            data-testid="google-reviews-all-link"
          >
            View all <ExternalLink size={12} strokeWidth={2} />
          </a>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.reviews.map((r, i) => (
            <motion.article
              key={`${r.author_name}-${i}`}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              className="rounded-lg border border-white/5 bg-[#1f2935] p-5"
              data-testid={`google-review-${i}`}
            >
              <div className="flex items-center gap-3">
                {r.profile_photo_url ? (
                  <img
                    src={r.profile_photo_url}
                    alt={r.author_name}
                    className="h-9 w-9 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-[#2a3744] border border-white/10 flex items-center justify-center text-[#D4AF37] text-xs">
                    {r.author_name?.[0] || "?"}
                  </div>
                )}
                <div>
                  <div className="text-[13px] text-white">{r.author_name}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.relative_time_description}
                  </div>
                </div>
              </div>
              <div className="mt-3 flex">
                {[...Array(5)].map((_, j) => (
                  <Star
                    key={j}
                    size={12}
                    strokeWidth={0}
                    fill={j < r.rating ? "#D4AF37" : "#334155"}
                    className={
                      j < r.rating ? "text-[#D4AF37]" : "text-[#334155]"
                    }
                  />
                ))}
              </div>
              <p className="mt-3 text-[13px] text-slate-300 leading-relaxed line-clamp-6">
                {r.text}
              </p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};
